import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "../../shared/Modal";
import { Loading } from "../../shared/Loading";
import { ErrorState } from "../../shared/ErrorState";
import { EmptyState } from "../../shared/EmptyState";
import { ActionButton } from "../../shared/ActionButton";
import { Avatar, Icons } from "../../shared";
import {
  professionalsApi,
  Professional,
} from "../../../services/professionalsApi";
import { appointmentsApi } from "../../../services/appointmentsApi";

interface StepProfessionalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepId: string;
  stepName: string;
  appointmentId: string;
  companyId: number;
  onProfessionalsUpdated?: () => void;
  /** When true (company user), show button to delete the step */
  isCompanyUser?: boolean;
  onStepDeleted?: () => void;
}

export const StepProfessionalsModal: React.FC<StepProfessionalsModalProps> = ({
  isOpen,
  onClose,
  stepId,
  stepName,
  appointmentId,
  companyId,
  onProfessionalsUpdated,
  isCompanyUser = false,
  onStepDeleted,
}) => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [assignedProfessionals, setAssignedProfessionals] = useState<
    Professional[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingProfessional, setUpdatingProfessional] = useState<
    number | null
  >(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasChangesRef = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load company professionals and assigned professionals for this step
      const [professionalsResponse, assignedResponse] = await Promise.all([
        professionalsApi.getProfessionalsByCompany(),
        appointmentsApi.getStepProfessionals(stepId),
      ]);

      setProfessionals(professionalsResponse.data || []);
      setAssignedProfessionals(assignedResponse.data || []);
    } catch (error) {
      console.error("Error loading professionals:", error);
      setError("Erro ao carregar profissionais. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [stepId]);

  useEffect(() => {
    if (isOpen) {
      hasChangesRef.current = false;
      loadData();
    }
  }, [isOpen, stepId, loadData]);

  const handleToggleProfessional = async (professional: Professional) => {
    setUpdatingProfessional(professional.id);

    try {
      const isAssigned = assignedProfessionals.some(
        (p) => p.id === professional.id
      );

      if (isAssigned) {
        // Remove professional from step
        await appointmentsApi.removeProfessionalFromStep(
          stepId,
          professional.id.toString()
        );
        setAssignedProfessionals((prev) =>
          prev.filter((p) => p.id !== professional.id)
        );
        hasChangesRef.current = true;
      } else {
        // Assign professional to step and start the step
        await appointmentsApi.assignProfessionalToStep(
          stepId,
          professional.id.toString()
        );
        setAssignedProfessionals((prev) => [...prev, professional]);
        // Start the step when first professional is assigned
        await appointmentsApi.startProgressStep(parseInt(stepId, 10));
        hasChangesRef.current = true;
        onProfessionalsUpdated?.();
      }
    } catch (error) {
      console.error("Error toggling professional:", error);
      setError("Erro ao atualizar profissional. Tente novamente.");
    } finally {
      setUpdatingProfessional(null);
    }
  };

  const isAssigned = (professional: Professional) =>
    assignedProfessionals.some((p) => p.id === professional.id);

  const handleClose = () => {
    if (hasChangesRef.current) {
      onProfessionalsUpdated?.();
    }
    onClose();
  };

  const handleDeleteStep = async () => {
    setDeleting(true);
    setError(null);
    try {
      const response = await appointmentsApi.deleteProgressStep(
        parseInt(stepId, 10)
      );
      if (response.success) {
        setDeleteConfirmOpen(false);
        onStepDeleted?.();
        onClose();
      } else {
        setError(response.error ?? "Erro ao excluir etapa.");
      }
    } catch (err) {
      setError("Erro ao excluir etapa. Tente novamente.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Gerenciar Profissionais"
      >
        <div className="p-8">
          <Loading />
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Gerenciar Profissionais"
      >
        <div className="p-6">
          <ErrorState message={error} onRetry={loadData} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Gerenciar Profissionais - ${stepName}`}
      size="lg"
    >
      <div className="p-6">
        {professionals.length === 0 ? (
          <>
            <EmptyState
              icon="👤"
              title="Nenhum profissional encontrado"
              description="Não há profissionais cadastrados no sistema."
            />
            <div className="mt-6 flex justify-between">
              <div>
                {isCompanyUser && (
                  <ActionButton
                    variant="danger"
                    size="sm"
                    icon={<Icons.Delete sx={{ fontSize: 18 }} />}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Excluir etapa
                  </ActionButton>
                )}
              </div>
              <ActionButton onClick={handleClose} variant="outline" size="sm">
                Fechar
              </ActionButton>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Selecione os profissionais que irão atuar nesta etapa do
                atendimento.
              </p>
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-medium">
                  {assignedProfessionals.length}
                </span>{" "}
                de <span className="font-medium">{professionals.length}</span>{" "}
                profissionais atribuídos
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {professionals.map((professional) => {
                const assigned = isAssigned(professional);
                const updating = updatingProfessional === professional.id;

                return (
                  <div
                    key={professional.id}
                    className={`p-4 border rounded-lg transition-all duration-200 ${
                      assigned
                        ? "border-green-200 bg-green-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar
                          text={professional.name}
                          src={professional.picture}
                          size="md"
                          variant={assigned ? "emerald" : "gray"}
                        />
                        <h3 className="font-medium text-gray-900">
                          {professional.name}
                        </h3>
                      </div>

                      <div className="ml-4">
                        <ActionButton
                          onClick={() => handleToggleProfessional(professional)}
                          variant={assigned ? "danger" : "primary"}
                          size="sm"
                          disabled={updating}
                          className={
                            assigned ? "bg-red-500 hover:bg-red-600" : ""
                          }
                        >
                          {updating ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                              {assigned ? "Removendo..." : "Atribuindo..."}
                            </div>
                          ) : assigned ? (
                            "Remover"
                          ) : (
                            "Atribuir"
                          )}
                        </ActionButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-between">
              <div>
                {isCompanyUser && (
                  <ActionButton
                    variant="danger"
                    size="sm"
                    icon={<Icons.Delete sx={{ fontSize: 18 }} />}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Excluir etapa
                  </ActionButton>
                )}
              </div>
              <ActionButton onClick={handleClose} variant="outline" size="sm">
                Fechar
              </ActionButton>
            </div>
          </>
        )}
      </div>

      {/* Delete step confirmation */}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        title="Excluir etapa"
        icon={<Icons.Delete sx={{ fontSize: 24 }} />}
        headerGradient="gray"
        size="sm"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Tem certeza que deseja excluir a etapa &quot;{stepName}&quot;? Esta
            ação não pode ser desfeita.
          </p>
          <div className="flex gap-3 justify-end">
            <ActionButton
              variant="secondary"
              size="sm"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant="danger"
              size="sm"
              icon={<Icons.Delete sx={{ fontSize: 18 }} />}
              disabled={deleting}
              onClick={handleDeleteStep}
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </ActionButton>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default StepProfessionalsModal;
